import React, {useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {GridColDef, GridRowSelectionModel} from "@mui/x-data-grid";
import {useSearchParams} from "react-router-dom";
import {toast} from "sonner";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, ListPageHeader, RowActions, SearchInput} from "../../ui/components";
import BatchDeleteAssetsDialog from "./BatchDeleteAssetsDialog";
import {useCreateAsset} from "../../apis/requests/assets/createAsset";
import DeleteResourceDialog from "../../components/DeleteResourceDialog";
import {useGetAssets} from "../../apis/requests/assets/getAssets";
import {ReachAsset} from "../../apis/requests/assets/types";
import {useUpdateAsset} from "../../apis/requests/assets/updateAsset";
import {ASSET_SOURCE_DISPLAY, formatAssetSize} from "./assetDisplay";
import {AssetsBatchActions} from "./AssetsBatchActions";
import RenameAssetDialog from "./RenameAssetDialog";
import UploadAssetDialog, {UploadAssetResult} from "./UploadAssetDialog";

const AssetsPage = () => {
  const {data, isLoading, error, refetch} = useGetAssets();
  const {mutate: updateAsset, mutateAsync: updateAssetAsync} = useUpdateAsset();
  const {mutate: createAsset, isPending: isUploading} = useCreateAsset();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 25});
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);

  const filteredAssets = useMemo(() => {
    const all: ReachAsset[] = data ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((asset) => !q || asset.name.toLowerCase().includes(q) || asset.url.toLowerCase().includes(q));
  }, [data, search]);

  const clearSelection = () => setSelectionModel([]);

  const selectedAssetIds = (selectionModel as (number | string)[]).map(Number);
  const selectedCount = selectedAssetIds.length;

  const pageAssets = useMemo(
    () =>
      filteredAssets.slice(
        paginationModel.page * paginationModel.pageSize,
        (paginationModel.page + 1) * paginationModel.pageSize
      ),
    [filteredAssets, paginationModel]
  );

  const pageSelectedCount = useMemo(
    () => pageAssets.filter((asset) => selectedAssetIds.includes(asset.id)).length,
    [pageAssets, selectedAssetIds]
  );

  const checkboxState: "checked" | "indeterminate" | "unchecked" =
    pageSelectedCount === 0
      ? "unchecked"
      : pageSelectedCount >= pageAssets.length
        ? "checked"
        : "indeterminate";

  const handleToggleAll = () => {
    if (checkboxState === "checked") {
      clearSelection();
    } else {
      setSelectionModel(pageAssets.map((asset) => asset.id));
    }
  };

  const onBatchDelete = () => {
    NiceModal.show(BatchDeleteAssetsDialog, {
      assets: filteredAssets.filter((asset) => selectedAssetIds.includes(asset.id)),
      onDeleted: async () => {
        clearSelection();
        await refetch();
      },
    });
  };

  const onAddAsset = () => {
    NiceModal.show(UploadAssetDialog, {
      onConfirm: (result: UploadAssetResult) =>
        createAsset(result, {
          onSuccess: async (asset) => {
            toast.success(`"${asset.name}" added`);
            await refetch();
          },
        }),
    });
  };

  const onRename = (asset: ReachAsset) => {
    NiceModal.show(RenameAssetDialog, {
      currentName: asset.name,
      onConfirm: (name: string) =>
        updateAsset({id: asset.id, name}, {onSuccess: () => toast.success(`Renamed to "${name}"`)}),
    });
  };

  const onReplace = (asset: ReachAsset) => {
    NiceModal.show(DeleteResourceDialog, {
      kind: "asset",
      mode: "replace",
      resourceId: asset.id,
      resourceName: asset.name,
    });
  };

  const onCopyUrl = async (asset: ReachAsset) => {
    await navigator.clipboard.writeText(asset.url);
    toast.success("Asset URL copied");
  };

  const onDelete = (asset: ReachAsset) => {
    NiceModal.show(DeleteResourceDialog, {
      kind: "asset",
      resourceId: asset.id,
      resourceName: asset.name,
    });
  };

  const columns = useMemo<GridColDef<ReachAsset>[]>(
    () => [
      {
        field: "preview",
        headerName: "",
        width: 64,
        sortable: false,
        filterable: false,
        renderCell: ({row}) => (
          <Box
            component="img"
            src={row.url}
            alt={row.name}
            sx={{width: 40, height: 40, objectFit: "contain", borderRadius: "4px"}}
          />
        ),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        editable: true,
        renderCell: ({row}) => (
          <Text
            size={14}
            variant="inter400"
            sx={{color: Colors.grey._900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.name}
          </Text>
        ),
      },
      {
        field: "source",
        headerName: "Source",
        flex: 1,
        renderCell: ({row}) => {
          const display = ASSET_SOURCE_DISPLAY[row.source];
          return <Chip size_="small" variant_={display.variant} label={display.label} />;
        },
      },
      {
        field: "content_type",
        headerName: "Type",
        flex: 1,
        renderCell: ({row}) => (
          <Text size={13} sx={{color: Colors.grey._700, fontFamily: "monospace"}}>
            {row.content_type}
          </Text>
        ),
      },
      {
        field: "size_bytes",
        headerName: "Size",
        flex: 0.7,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._700}}>
            {formatAssetSize(row.size_bytes)}
          </Text>
        ),
      },
      {
        field: "principal",
        headerName: "Added by",
        flex: 1,
        filterable: false,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        filterable: false,
        renderCell: ({row}) => (
          <RowActions
            onDelete={() => onDelete(row)}
            onOpenInNewTab={() => window.open(row.url, "_blank")}
            extraActions={[
              {
                label: "Rename",
                icon: <DriveFileRenameOutlineIcon sx={{width: 20, height: 20}} />,
                onClick: () => onRename(row),
              },
              {
                label: "Copy URL",
                icon: <ContentCopyIcon sx={{width: 20, height: 20}} />,
                onClick: () => onCopyUrl(row),
              },
              {
                label: "Replace everywhere",
                icon: <SwapHorizIcon sx={{width: 20, height: 20}} />,
                onClick: () => onReplace(row),
              },
              ...(row.source_url
                ? [
                    {
                      label: "Open original in Customer.io",
                      icon: <OpenInNewIcon sx={{width: 20, height: 20}} />,
                      onClick: () => window.open(row.source_url as string, "_blank"),
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Assets">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPaginationModel((m) => ({...m, page: 0}));
          }}
          placeholder="Search assets..."
        />
        <Button buttonType="contained-primary" size="small" onClick={onAddAsset} disabled={isUploading}>
          {isUploading ? "Adding..." : "+ Add asset"}
        </Button>
      </ListPageHeader>
      {error ? (
        <Text size={14}>Failed to load assets.</Text>
      ) : (
        <ListDataGrid
          rows={filteredAssets}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          initialState={{sorting: {sortModel: [{field: "name", sort: "asc"}]}}}
          processRowUpdate={async (updated: ReachAsset, original: ReachAsset) => {
            if (updated.name === original.name) return original;
            return updateAssetAsync({id: updated.id, name: updated.name});
          }}
          onProcessRowUpdateError={() => toast.error("Failed to rename asset")}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
            clearSelection();
          }}
          columnHeaderHeight={44}
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          selectionBar={
            selectedCount > 0 ? (
              <AssetsBatchActions
                selectedCount={selectedCount}
                checkboxState={checkboxState}
                onToggleAll={handleToggleAll}
                onDelete={onBatchDelete}
              />
            ) : undefined
          }
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnMenu={false}
          noRowsText={search.trim() ? "No assets match your search" : "No assets yet"}
        />
      )}
    </Box>
  );
};

export default AssetsPage;
